import React, { useState, useCallback, useEffect } from 'react';
import {
  IonContent,
} from '@ionic/react';
import Toaster, { ToasterVariant } from '@/components/common/Toaster';

import OfflineOperationsPanel from '../components/offline/OfflineOperationsPanel';
import SyncComparisonList from '@/components/offline/SyncComparisonList';
import SyncComparisonModal from '@/components/offline/SyncComparisonModal';
import PageLayout from '@/components/navigation/PageLayout';
import { usePageTitle } from '@/contexts/PageTitleContext';
import { syncService } from '@/services/offline/SyncService';
import { SyncComparisonResult, ApprovalChoice } from '@/types/model/sync';

const OfflineOperations: React.FC = () => {
  usePageTitle('Offline Operations');
  const [comparisons, setComparisons] = useState<SyncComparisonResult[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [selectedComparison, setSelectedComparison] = useState<SyncComparisonResult | null>(null);
  const [showComparisonModal, setShowComparisonModal] = useState(false);
  const [showToast, setShowToast] = useState(false);
  const [toastMessage, setToastMessage] = useState('');
  const [toastVariant, setToastVariant] = useState<ToasterVariant>('success');
  const hasPendingConflicts = comparisons.some(c => c.status === 'conflict');

  const loadComparisons = useCallback(async (): Promise<void> => {
    setIsLoading(true);
    try {
      const results = await syncService.fetchSyncComparisons();
      setComparisons(results);
    } catch (error) {
      console.error('Failed to load sync comparisons:', error);
      setToastMessage('Failed to load sync data.');
      setToastVariant('error');
      setShowToast(true);
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    loadComparisons();
  }, [loadComparisons]);

  const handleSelectInspection = (item: SyncComparisonResult): void => {
    setSelectedComparison(item);
    setShowComparisonModal(true);
  };

  const handleCommit = async (inspectionResultId: number, choice: ApprovalChoice): Promise<void> => {
    const comparison = comparisons.find(c => c.inspectionResultId === inspectionResultId);
    if (!comparison) return;

    if (choice === 'local' && comparison.localInspection && comparison.serverInspection) {
      console.log('Committing local changes to server for inspectionResultId:', inspectionResultId);
      // console.log('Local inspection data:', comparison.localInspection);
      // console.log('Server inspection data:', comparison.serverInspection);
      await syncService.commitLocalToServer(comparison.localInspection, comparison.serverInspection);
    } else if (choice === 'server') {
      await syncService.overrideLocalWithServer(inspectionResultId);
    }
    await loadComparisons();
  };

  const handleOverrideLocal = async (ids: number[]): Promise<void> => {
    try {
      for (const id of ids) {
        await syncService.overrideLocalWithServer(id);
      }
      setToastMessage(`${ids.length} inspection(s) overridden with server data.`);
      setToastVariant('success');
      setShowToast(true);
      await loadComparisons();
    } catch (error) {
      console.error('Bulk override failed:', error);
      setToastMessage('Failed to override some inspections.');
      setToastVariant('error');
      setShowToast(true);
    }
  };

  const handleDeleteLocal = async (ids: number[]): Promise<void> => {
    try {
      for (const id of ids) {
        await syncService.deleteLocalInspection(id);
      }
      setToastMessage(`${ids.length} cached inspection(s) deleted.`);
      setToastVariant('success');
      setShowToast(true);
      await loadComparisons();
    } catch (error) {
      console.error('Bulk delete failed:', error);
      setToastMessage('Failed to delete some inspections.');
      setToastVariant('error');
      setShowToast(true);
    }
  };

  const handleClearAllCache = async (): Promise<void> => {
    try {
      await syncService.clearCachedContractData();
      setComparisons([]);
      setToastMessage('Cached module data cleared.');
      setToastVariant('success');
      setShowToast(true);
    } catch (error) {
      console.error('Clear cache failed:', error);
      setToastMessage('Failed to clear cached data.');
      setToastVariant('error');
      setShowToast(true);
    }
  };

  return (
    <PageLayout>

      <IonContent fullscreen className="ion-padding">
        <div style={{ maxWidth: '1200px', margin: '0 auto' }}>
          <OfflineOperationsPanel onClearCache={handleClearAllCache} hasPendingConflicts={hasPendingConflicts} />

          <div style={{ margin: '16px 0' }}>
            <SyncComparisonList
              comparisons={comparisons}
              isLoading={isLoading}
              onSelectInspection={handleSelectInspection}
              onOverrideLocal={handleOverrideLocal}
              onDeleteLocal={handleDeleteLocal}
              onRefresh={loadComparisons}
            />
          </div>
        </div>

        <SyncComparisonModal
          isOpen={showComparisonModal}
          onDidDismiss={() => setShowComparisonModal(false)}
          comparisonData={selectedComparison}
          onCommit={handleCommit}
        />

        <Toaster
          isOpen={showToast}
          onDidDismiss={() => setShowToast(false)}
          message={toastMessage}
          variant={toastVariant}
        />
      </IonContent>
    </PageLayout>
  );
};

export default OfflineOperations;